import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { useLibraryStories } from '@/hooks/useLibrary'
import type { LibraryStory } from '@/types/library'
import { STAGE_PCT, type BuildStage } from '@/components/feed/feedTypes'
import { ROUTES } from './nav'
import '@/styles/library2.css'

/** What is still to record (rec-pick.js v2): writing, or built and not
 *  recorded within the week; a story sent from the desktop always counts. */
export function recordTodo(stories: LibraryStory[]): LibraryStory[] {
  return stories
    .filter((s) => s.stage === 'writing' || (s.stage === 'todo' && (!s.stale || s.toPhoneAt)))
    .sort((a, b) => (b.toPhoneAt ?? 0) - (a.toPhoneAt ?? 0) || b.at - a.at)
}

/**
 * Record ＋, Brian's after/rec-pick.js v2 (Oct 21): "when they push the button
 * to start a recording, it'd select from the list of available stories". A
 * frosted sheet over whatever screen is open; a tap above it or Esc closes it.
 * Only what's still to record: a story sent from the desktop first, then the
 * rest newest first; one still being written shows how far along it is.
 * Recorded and posted stories, and a story built over a week ago and never
 * recorded, live in the Library. A tap opens the camera with that script.
 */
export function RecordPickSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { stories, isLoading } = useLibraryStories()

  // The tap that opened the sheet must not land on a row as it slides in
  // under the finger (a phone's delayed click).
  const openedAt = useRef(0)
  useEffect(() => { if (open) openedAt.current = Date.now() }, [open])
  useEffect(() => {
    if (!open) return
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [open, onClose])

  const todo = recordTodo(stories)
  const built = stories.filter((s) => s.stage !== 'writing')
  const done = built.filter((s) => s.stage === 'recorded' || s.stage === 'posted').length
  const old = built.filter((s) => s.stage === 'todo' && s.stale && !s.toPhoneAt).length
  const sent = todo.filter((s) => s.toPhoneAt)
  const rest = todo.filter((s) => !s.toPhoneAt)

  const go = (to: string) => { if (Date.now() - openedAt.current < 450) return; onClose(); navigate(to) }

  const row = (s: LibraryStory) => {
    const writing = s.stage === 'writing'
    const pct = STAGE_PCT[((s.story as { build_stage?: BuildStage | null } | null)?.build_stage) ?? 'queued'] ?? 3
    return (
      <button key={s.id} type="button" className={`rpick__it${writing ? ' is-writing' : ''}`} disabled={writing}
        onClick={() => go(`${ROUTES.record}?story=${encodeURIComponent(s.id)}`)}>
        <TopicGlyph topic={s.topic} size={24} />
        <div className="rpick__t">
          <div className="rpick__h">{s.headline}</div>
          <div className={`rpick__s${s.toPhoneAt ? ' rpick__s--amber' : ''}`}>
            {writing ? `Emma is writing it · ${pct}%` : s.toPhoneAt ? 'Sent from your computer · script ready' : 'Ready to record'}
          </div>
          {writing && <div className="rpick__bar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>}
        </div>
        <span className="rpick__go">Record</span>
      </button>
    )
  }

  const libTo = `${ROUTES.library}?f=${done ? 'recorded' : 'todo'}`
  const foot = (done || old) ? (
    <Link className="rpick__lib" to={libTo} onClick={(e) => { e.preventDefault(); go(libTo) }}>
      {[done ? `${done} recorded` : '', old ? `${old} older` : ''].filter(Boolean).join(' · ')} · in your Library
    </Link>
  ) : null

  return (
    <>
      <div className={`rpick-scrim${open ? ' is-on' : ''}`} onClick={onClose} aria-hidden="true" />
      <div className={`rpick__sheet rpick__sheet--over${open ? ' is-on' : ''}`} role="dialog" aria-modal="true"
        aria-label="Choose a story to record" aria-hidden={!open}>
        <div className="rpick__hd">
          <h2>Choose a story to record</h2>
          <p>The stories you built. Tap one to open the camera with its script.</p>
        </div>
        <div className="rpick__ls">
          {isLoading ? <div className="rpick__none">Opening your stories.</div>
            : !todo.length ? (
              <div className="rpick__none">
                <b>{built.length ? 'Nothing left to record' : 'Nothing to record yet'}</b>
                Build a story from your feed and it lands here, ready to record.<br />
                <Link to={ROUTES.feed} onClick={(e) => { e.preventDefault(); go(ROUTES.feed) }}>Go to your feed</Link>
              </div>
            ) : (
              <>
                {sent.length > 0 && <><div className="rpick__sec">Sent to your phone</div>{sent.map(row)}</>}
                {rest.length > 0 && <><div className="rpick__sec">To record</div>{rest.map(row)}</>}
              </>
            )}
          {foot}
        </div>
      </div>
    </>
  )
}
